import { useState, useRef, useEffect } from 'react';
import { useNotifications, type Notification, type NotificationCategory } from '@/hooks/useNotifications';
import { useNavigate } from 'react-router-dom';

interface Props {
  open: boolean;
  onClose: () => void;
}

const typeIcon: Record<string, string> = {
  info:    'ri-information-line',
  success: 'ri-checkbox-circle-line',
  warning: 'ri-error-warning-line',
  error:   'ri-close-circle-line',
};

const typeColor: Record<string, string> = {
  info:    'text-sky-500',
  success: 'text-emerald-500',
  warning: 'text-amber-500',
  error:   'text-rose-500',
};

const catLabel: Record<string, string> = {
  lead:        'Lead',
  payment:     'Payment',
  signature:   'Signature',
  addon:       'Add-on',
  delivery:    'Delivery',
  appointment: 'Appointment',
  system:      'System',
};

const catIcon: Record<string, string> = {
  lead:        'ri-user-received-line',
  payment:     'ri-money-dollar-circle-line',
  signature:   'ri-file-text-line',
  addon:       'ri-puzzle-line',
  delivery:    'ri-send-plane-line',
  appointment: 'ri-calendar-event-line',
  system:      'ri-settings-3-line',
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'ahora';
  if (m < 60) return `hace ${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `hace ${h}h`;
  const d = Math.floor(h / 24);
  return `hace ${d}d`;
}

function entityRoute(n: Notification): string | null {
  if (!n.entity_type || !n.entity_id) return null;
  switch (n.entity_type) {
    case 'contract':     return `/admin/contracts/${n.entity_id}`;
    case 'payment':      return `/admin/payments`;
    case 'appointment':  return `/admin/appointments`;
    case 'lead':         return `/admin/leads/${n.entity_id}`;
    case 'job':          return `/admin/jobs/${n.entity_id}`;
    case 'client':       return `/admin/clients/${n.entity_id}`;
    default:             return null;
  }
}

export default function NotificationDropdown({ open, onClose }: Props) {
  const navigate = useNavigate();
  const { notifications, unreadCount, loading, markAsRead, markAllAsRead } = useNotifications();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onClose]);

  if (!open) return null;

  const handleItemClick = (n: Notification) => {
    if (!n.is_read) markAsRead(n.id);
    const route = entityRoute(n);
    if (route) {
      onClose();
      navigate(route);
    }
  };

  return (
    <div
      ref={ref}
      className="absolute right-0 top-full mt-2 w-80 bg-white border border-slate-200 rounded-xl shadow-2xl z-50 overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <span className="text-slate-800 font-semibold text-sm">Notificaciones</span>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="text-xs text-[#C8A96E] hover:text-[#b8965a] cursor-pointer whitespace-nowrap transition-colors"
            >
              Marcar todas
            </button>
          )}
          <button
            onClick={() => { onClose(); navigate('/admin/notifications'); }}
            className="text-xs text-slate-400 hover:text-slate-600 cursor-pointer whitespace-nowrap transition-colors"
          >
            Ver todo
          </button>
        </div>
      </div>

      {/* List */}
      <div className="max-h-[360px] overflow-y-auto divide-y divide-slate-50">
        {loading && (
          <div className="flex items-center justify-center py-10">
            <i className="ri-loader-4-line animate-spin text-slate-400 text-xl"></i>
          </div>
        )}

        {!loading && notifications.length === 0 && (
          <div className="flex flex-col items-center justify-center py-10 gap-2">
            <i className="ri-notification-off-line text-slate-200 text-3xl"></i>
            <p className="text-slate-400 text-xs">Sin notificaciones</p>
          </div>
        )}

        {!loading && notifications.map((n: Notification) => (
          <div
            key={n.id}
            onClick={() => handleItemClick(n)}
            className={`flex gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-slate-50 ${
              !n.is_read ? 'bg-amber-50/30' : ''
            }`}
          >
            {/* Icon */}
            <div className={`mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
              !n.is_read ? 'bg-slate-50' : 'bg-white'
            }`}>
              <i className={`${
                n.category && catIcon[n.category]
                  ? catIcon[n.category]
                  : (typeIcon[n.type] ?? typeIcon.info)
              } text-base ${typeColor[n.type] ?? typeColor.info}`}></i>
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <p className={`text-xs font-semibold leading-tight truncate ${
                  n.is_read ? 'text-slate-400' : 'text-slate-800'
                }`}>
                  {n.title}
                </p>
                {!n.is_read && (
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C8A96E] shrink-0 mt-1"></span>
                )}
              </div>
              <p className={`text-xs mt-0.5 leading-snug line-clamp-2 ${
                n.is_read ? 'text-slate-400' : 'text-slate-500'
              }`}>
                {n.message}
              </p>
              <div className="flex items-center gap-2 mt-1">
                {n.category && (
                  <span className="text-[10px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-md">
                    {catLabel[n.category] ?? n.category}
                  </span>
                )}
                <p className="text-[10px] text-slate-300">{timeAgo(n.created_at)}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <div className="px-4 py-2.5 border-t border-slate-100 text-center">
          <span className="text-[10px] text-slate-400">
            {notifications.length === 50 ? 'Mostrando las últimas 50' : `${notifications.length} notificacion${notifications.length !== 1 ? 'es' : ''}`}
          </span>
        </div>
      )}
    </div>
  );
}